import { Component, input } from '@angular/core';
import { Router } from '@angular/router';
import {
  ConnectorsStatusesType,
  DataSourceApprovalStatus,
  DataSourcesStatusesType,
  ERROR_TOOLTIP_STATUSES,
  OrganizationConnectorItemDTO,
  OrganizationDataSourceItemDTO,
  OrganizationPipelineItemDTO,
  PipelinesStatusesType,
} from '@core/models';
import { isMatchingDataSourceApprovalStatus } from '@core/utils';
import { AccountService } from '@features/server-side-authentication';
import { OrganizationTableDataDTO } from '../../models';

const routeMap: Record<string, string> = {
  ingest: 'connectors',
  publish: 'pipelines',
  discover: 'data-sources',
};

@Component({
  selector: 'app-status-table-organizations',
  templateUrl: './status-table-organizations.component.html',
  styleUrl: './status-table-organizations.component.scss',
  standalone: false,
})
export class StatusTableOrganizationsComponent {
  errorStatuses!: (ConnectorsStatusesType | DataSourcesStatusesType | PipelinesStatusesType)[];

  public data = input.required<OrganizationTableDataDTO, OrganizationTableDataDTO>({
    transform: (value: OrganizationTableDataDTO) => {
      switch (value.title) {
        case 'Ingest':
          this.errorStatuses = ERROR_TOOLTIP_STATUSES.Connectors;
          break;
        case 'Publish':
          this.errorStatuses = ERROR_TOOLTIP_STATUSES.Pipelines;
          break;
        case 'Discover':
          this.errorStatuses = ERROR_TOOLTIP_STATUSES.DataSources;
          break;
      }

      return value;
    },
  });

  constructor(
    private router: Router,
    private readonly accountService: AccountService,
  ) {}

  get statusColumnName(): string {
    return this.isConnectors() ? 'Status' : 'Connect status';
  }

  errorStatusMessage(
    element: OrganizationConnectorItemDTO | OrganizationDataSourceItemDTO | OrganizationPipelineItemDTO,
  ): string {
    switch (this.data().title) {
      case 'Ingest':
        return (element as OrganizationConnectorItemDTO).last_connector_run_error_message;

      case 'Publish':
        return (element as OrganizationPipelineItemDTO).error_message;

      case 'Discover':
      default:
        return (element as OrganizationDataSourceItemDTO).last_discovery_run_error_message;
    }
  }

  isTooltipDisabled(status: string): boolean {
    const statusLocal = status.toLowerCase() as
      | ConnectorsStatusesType
      | DataSourcesStatusesType
      | PipelinesStatusesType;
    return !this.errorStatuses.includes(statusLocal);
  }

  isConnectors(): boolean {
    return this.data().title === 'Ingest';
  }

  onSeeMore(url: string) {
    this.router.navigateByUrl(url);
  }

  redirectOnClick(
    row: OrganizationConnectorItemDTO | OrganizationDataSourceItemDTO | OrganizationPipelineItemDTO,
  ): void {
    if (!this.data().organizationId) return;

    const route = routeMap[this.data().title.toLowerCase()];
    const basePath = this.accountService.isInternal ? `organizations/${this.data().organizationId}/${route}` : route;

    const targetUrl = !this.canExpandDataSourceRow(row) ? [basePath] : [`${basePath}/${row.id}`];

    this.router.navigate(targetUrl);
  }

  canExpandDataSourceRow(
    row: OrganizationConnectorItemDTO | OrganizationDataSourceItemDTO | OrganizationPipelineItemDTO,
  ): boolean {
    return !(
      'approval_status' in row && isMatchingDataSourceApprovalStatus(row.approval_status, DataSourceApprovalStatus.New)
    );
  }
}
